import moment from 'moment-timezone';

/*
 * Demo dataset for the Marketing insights screens. Every displayed metric
 * (CPA, ROAS, conversion, AOV, rebook) is DERIVED from the primitives below,
 * never hardcoded, so cards and tables can never disagree with each other.
 */

export type ChannelBucket =
    | 'paid_search'
    | 'paid_social'
    | 'organic_search'
    | 'google_business_profile'
    | 'word_of_mouth'
    | 'referral_partner'
    | 'walk_in'
    | 'directory'
    | 'unattributed';

export interface MarketingSource {
    id: string;
    name: string;
    bucket: ChannelBucket;
}

export interface DemoLocation {
    id: number;
    name: string;
}

export const CHANNEL_COLORS: Record<ChannelBucket, string> = {
    paid_search: '#5E8677',
    paid_social: '#92B4A7',
    organic_search: '#C18733',
    google_business_profile: '#EAB464',
    word_of_mouth: '#BAA997',
    referral_partner: '#9C8267',
    walk_in: '#F2D2A2',
    directory: '#BCB8AE',
    unattributed: '#A3A3A3'
};

export const CHANNEL_LABELS: Record<ChannelBucket, string> = {
    paid_search: 'Paid Search',
    paid_social: 'Paid Social',
    organic_search: 'Organic Search',
    google_business_profile: 'Google Business',
    word_of_mouth: 'Word of Mouth',
    referral_partner: 'Partner Referral',
    walk_in: 'Walk-in',
    directory: 'Directories',
    unattributed: 'Unattributed'
};

export const DEMO_LOCATIONS: Array<DemoLocation> = [
    { id: 1, name: 'Downtown Austin' },
    { id: 2, name: 'South Congress' },
    { id: 3, name: 'Round Rock' },
    { id: 4, name: 'Cedar Park' },
    { id: 5, name: 'Georgetown' }
];

export const SOURCES: Array<MarketingSource> = [
    { id: 'google_ads', name: 'Google Ads', bucket: 'paid_search' },
    { id: 'meta_ads', name: 'Facebook & Instagram Ads', bucket: 'paid_social' },
    { id: 'google_organic', name: 'Google Search (Organic)', bucket: 'organic_search' },
    { id: 'gbp', name: 'Google Business Profile', bucket: 'google_business_profile' },
    { id: 'friend_referral', name: 'Friend Referral', bucket: 'word_of_mouth' },
    { id: 'vet_referral', name: 'Vet Referral', bucket: 'referral_partner' },
    { id: 'walk_in', name: 'Walk-in', bucket: 'walk_in' },
    { id: 'yelp', name: 'Yelp', bucket: 'directory' },
    { id: 'unattributed', name: 'Unattributed', bucket: 'unattributed' }
];

/*
 * Per-source behavior primitives. Conversion, first-visit AOV, 90-day revenue
 * multiplier, rebook rate and days-to-book differ by channel on purpose: the
 * demo story is that channels bring clients of very different quality.
 */
interface SourceProfile {
    convRate: number;
    aovFirst: number;
    rev90Multiplier: number;
    rebookRate: number;
    medianDaysToBook: number;
    momGrowth: number;
}

const SOURCE_PROFILES: Record<string, SourceProfile> = {
    google_ads: {
        convRate: 0.34,
        aovFirst: 78,
        rev90Multiplier: 2.1,
        rebookRate: 0.54,
        medianDaysToBook: 3,
        momGrowth: 0.12
    },
    meta_ads: {
        convRate: 0.27,
        aovFirst: 71,
        rev90Multiplier: 1.8,
        rebookRate: 0.48,
        medianDaysToBook: 5,
        momGrowth: 0.18
    },
    google_organic: {
        convRate: 0.41,
        aovFirst: 84,
        rev90Multiplier: 2.6,
        rebookRate: 0.63,
        medianDaysToBook: 4,
        momGrowth: 0.05
    },
    gbp: { convRate: 0.52, aovFirst: 88, rev90Multiplier: 2.9, rebookRate: 0.67, medianDaysToBook: 2, momGrowth: 0.09 },
    friend_referral: {
        convRate: 0.58,
        aovFirst: 92,
        rev90Multiplier: 3.4,
        rebookRate: 0.74,
        medianDaysToBook: 6,
        momGrowth: 0.03
    },
    vet_referral: {
        convRate: 0.62,
        aovFirst: 105,
        rev90Multiplier: 3.8,
        rebookRate: 0.78,
        medianDaysToBook: 7,
        momGrowth: 0.06
    },
    walk_in: {
        convRate: 0.71,
        aovFirst: 64,
        rev90Multiplier: 2.2,
        rebookRate: 0.58,
        medianDaysToBook: 0,
        momGrowth: -0.02
    },
    yelp: {
        convRate: 0.31,
        aovFirst: 74,
        rev90Multiplier: 1.9,
        rebookRate: 0.51,
        medianDaysToBook: 5,
        momGrowth: -0.05
    },
    unattributed: {
        convRate: 0.45,
        aovFirst: 76,
        rev90Multiplier: 2.3,
        rebookRate: 0.6,
        medianDaysToBook: 4,
        momGrowth: 0.01
    }
};

/*
 * New clients (last 30 days) per location x source. Each location has a
 * deliberately different acquisition mix so the comparison table tells a
 * story on its own: Downtown is ads-heavy, Cedar Park lives off walk-ins,
 * Georgetown off vet referrals.
 */
const NEW_CLIENTS: Record<number, Record<string, number>> = {
    1: {
        google_ads: 18,
        meta_ads: 9,
        google_organic: 7,
        gbp: 8,
        friend_referral: 6,
        vet_referral: 3,
        walk_in: 4,
        yelp: 3,
        unattributed: 16
    },
    2: {
        google_ads: 6,
        meta_ads: 11,
        google_organic: 5,
        gbp: 9,
        friend_referral: 8,
        vet_referral: 2,
        walk_in: 7,
        yelp: 2,
        unattributed: 11
    },
    3: {
        google_ads: 10,
        meta_ads: 5,
        google_organic: 6,
        gbp: 12,
        friend_referral: 7,
        vet_referral: 5,
        walk_in: 6,
        yelp: 1,
        unattributed: 13
    },
    4: {
        google_ads: 3,
        meta_ads: 2,
        google_organic: 4,
        gbp: 6,
        friend_referral: 5,
        vet_referral: 4,
        walk_in: 15,
        yelp: 1,
        unattributed: 8
    },
    5: {
        google_ads: 4,
        meta_ads: 3,
        google_organic: 5,
        gbp: 7,
        friend_referral: 6,
        vet_referral: 12,
        walk_in: 5,
        yelp: 2,
        unattributed: 9
    }
};

export type PaidChannel = 'google' | 'meta';

export interface Campaign {
    id: string;
    name: string;
    channel: PaidChannel;
    spend: number;
    newClients: number;
    locationIds: Array<number>;
}

/*
 * Campaign spend + clients are the primitives; CPA and ROAS are derived.
 * Campaign clients per channel sum to the same totals as the location matrix
 * (google_ads: 41, meta_ads: 30) so both screens always agree.
 */
export const CAMPAIGNS: Array<Campaign> = [
    {
        id: 'g1',
        name: 'Grooming - Brand - Austin',
        channel: 'google',
        spend: 1480,
        newClients: 12,
        locationIds: [1, 2]
    },
    {
        id: 'g2',
        name: 'Dog Grooming Near Me - PMax',
        channel: 'google',
        spend: 1720,
        newClients: 11,
        locationIds: [1, 3]
    },
    { id: 'g3', name: 'Daycare - Round Rock', channel: 'google', spend: 980, newClients: 7, locationIds: [3] },
    { id: 'g4', name: 'Boarding - Holidays', channel: 'google', spend: 1140, newClients: 6, locationIds: [1, 5] },
    { id: 'g5', name: 'Puppy First Groom - Search', channel: 'google', spend: 620, newClients: 4, locationIds: [2, 4] },
    { id: 'g6', name: 'Cat Grooming - Exact', channel: 'google', spend: 460, newClients: 1, locationIds: [1] },
    {
        id: 'm1',
        name: 'New Client Offer - IG Reels',
        channel: 'meta',
        spend: 1350,
        newClients: 13,
        locationIds: [1, 2]
    },
    { id: 'm2', name: 'Before/After Carousel', channel: 'meta', spend: 1080, newClients: 9, locationIds: [2, 3] },
    { id: 'm3', name: 'Daycare Open House', channel: 'meta', spend: 740, newClients: 5, locationIds: [3, 4] },
    { id: 'm4', name: 'Retargeting - Site Visitors', channel: 'meta', spend: 590, newClients: 3, locationIds: [1, 5] }
];

/* Deterministic PRNG so charts are stable across renders and reloads. */
const mulberry32 = (seed: number) => {
    let a = seed;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};

export interface SourceRow {
    source: MarketingSource;
    leads: number;
    newClients: number;
    convRate: number;
    medianDaysToBook: number;
    revenueFirst: number;
    revenue90d: number;
    aov: number;
    rebookRate: number;
    momDelta: number;
}

export interface OverviewData {
    totalNewClients: number;
    totalNewClientsDelta: number;
    totalRevenue90d: number;
    totalRevenue90dDelta: number;
    avgCac: number;
    avgCacDelta: number;
    unattributedRate: number;
    rows: Array<SourceRow>;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

const buildSourceRow = (source: MarketingSource, newClients: number): SourceRow => {
    const profile = SOURCE_PROFILES[source.id];
    const leads = Math.max(newClients, Math.round(newClients / profile.convRate));
    const revenueFirst = round2(newClients * profile.aovFirst);
    const revenue90d = round2(revenueFirst * profile.rev90Multiplier);

    return {
        source,
        leads,
        newClients,
        convRate: leads === 0 ? 0 : newClients / leads,
        medianDaysToBook: profile.medianDaysToBook,
        revenueFirst,
        revenue90d,
        aov: newClients === 0 ? 0 : round2(revenueFirst / newClients),
        rebookRate: profile.rebookRate,
        momDelta: profile.momGrowth
    };
};

export const getOverviewData = (locationIds: Array<number>): OverviewData => {
    const ids = locationIds.length === 0 ? DEMO_LOCATIONS.map(location => location.id) : locationIds;

    const rows = SOURCES.map(source => {
        const newClients = ids.reduce((sum, id) => sum + (NEW_CLIENTS[id]?.[source.id] ?? 0), 0);
        return buildSourceRow(source, newClients);
    }).sort((row, other) => other.newClients - row.newClients);

    const totalNewClients = rows.reduce((sum, row) => sum + row.newClients, 0);
    const totalRevenue90d = round2(rows.reduce((sum, row) => sum + row.revenue90d, 0));
    const unattributed = rows.find(row => row.source.id === 'unattributed');

    const spendInScope = CAMPAIGNS.filter(campaign => campaign.locationIds.some(id => ids.includes(id))).reduce(
        (sum, campaign) => sum + campaign.spend,
        0
    );
    const paidClients = rows
        .filter(row => row.source.id === 'google_ads' || row.source.id === 'meta_ads')
        .reduce((sum, row) => sum + row.newClients, 0);

    const weightedDelta =
        totalNewClients === 0 ? 0 : rows.reduce((sum, row) => sum + row.momDelta * row.newClients, 0) / totalNewClients;

    return {
        totalNewClients,
        totalNewClientsDelta: weightedDelta,
        totalRevenue90d,
        totalRevenue90dDelta: weightedDelta * 1.4,
        avgCac: paidClients === 0 ? 0 : round2(spendInScope / paidClients),
        avgCacDelta: -0.04,
        unattributedRate: totalNewClients === 0 ? 0 : (unattributed?.newClients ?? 0) / totalNewClients,
        rows
    };
};

export interface DailyPoint {
    date: string;
    spend: number;
    newClients: number;
}

export interface CampaignRow {
    campaign: Campaign;
    cpa: number;
    revenue90d: number;
    roas: number;
}

export interface PaidChannelData {
    spend: number;
    spendDelta: number;
    newClients: number;
    newClientsDelta: number;
    cpa: number;
    roas: number;
    daily: Array<DailyPoint>;
    campaigns: Array<CampaignRow>;
}

/*
 * 90-day revenue per paid client comes from the same source profiles used by
 * the overview screen, so ROAS here reconciles with the source table there.
 */
export const getPaidChannelData = (channel: PaidChannel): PaidChannelData => {
    const sourceId = channel === 'google' ? 'google_ads' : 'meta_ads';
    const profile = SOURCE_PROFILES[sourceId];
    const revenuePerClient = profile.aovFirst * profile.rev90Multiplier;

    const campaigns = CAMPAIGNS.filter(campaign => campaign.channel === channel);
    const spend = campaigns.reduce((sum, campaign) => sum + campaign.spend, 0);
    const newClients = campaigns.reduce((sum, campaign) => sum + campaign.newClients, 0);

    const campaignRows: Array<CampaignRow> = campaigns
        .map(campaign => {
            const revenue90d = round2(campaign.newClients * revenuePerClient);
            return {
                campaign,
                cpa: campaign.newClients === 0 ? 0 : round2(campaign.spend / campaign.newClients),
                revenue90d,
                roas: round2(revenue90d / campaign.spend)
            };
        })
        .sort((row, other) => other.campaign.spend - row.campaign.spend);

    const random = mulberry32(channel === 'google' ? 20260807 : 80702602);
    const weights = Array.from({ length: 30 }, () => 0.4 + random() * 1.2);
    const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);

    const daily: Array<DailyPoint> = weights.map((weight, index) => {
        const day = moment()
            .subtract(29 - index, 'days')
            .format('MM-DD');
        const dailySpend = round2((spend * weight) / weightTotal);
        const expectedClients = (newClients * weight) / weightTotal;
        const clients = Math.floor(expectedClients) + (random() < expectedClients % 1 ? 1 : 0);

        return { date: day, spend: dailySpend, newClients: clients };
    });

    return {
        spend,
        spendDelta: profile.momGrowth,
        newClients,
        newClientsDelta: profile.momGrowth + 0.04,
        cpa: newClients === 0 ? 0 : round2(spend / newClients),
        roas: spend === 0 ? 0 : round2((newClients * revenuePerClient) / spend),
        daily,
        campaigns: campaignRows
    };
};

export interface CohortRow {
    month: string;
    leads: number;
    newClients: number;
    convRate: number;
    medianDaysToBook: number;
    revenueFirst: number;
    revenue90d: number;
    lostClients: number;
}

export interface VelocityData {
    rows: Array<CohortRow>;
    totals: Omit<CohortRow, 'month' | 'medianDaysToBook' | 'convRate'> & { convRate: number; medianDaysToBook: number };
    movement: Array<{ month: string; gained: number; lost: number; netActive: number }>;
}

const COHORT_BASE = [
    { month: 'Jan', leads: 96, newClients: 41, medianDays: 5, lost: 12 },
    { month: 'Feb', leads: 104, newClients: 46, medianDays: 5, lost: 14 },
    { month: 'Mar', leads: 118, newClients: 53, medianDays: 4, lost: 11 },
    { month: 'Apr', leads: 111, newClients: 49, medianDays: 4, lost: 16 },
    { month: 'May', leads: 129, newClients: 58, medianDays: 4, lost: 13 },
    { month: 'Jun', leads: 138, newClients: 61, medianDays: 3, lost: 15 },
    { month: 'Jul', leads: 152, newClients: 68, medianDays: 3, lost: 12 },
    { month: 'Aug', leads: 61, newClients: 27, medianDays: 3, lost: 4 }
];

export const getVelocityData = (): VelocityData => {
    const blendedAov = 82;
    const blendedMultiplier = 2.6;

    const rows: Array<CohortRow> = COHORT_BASE.map(base => {
        const revenueFirst = round2(base.newClients * blendedAov);
        return {
            month: `${base.month} 2026`,
            leads: base.leads,
            newClients: base.newClients,
            convRate: base.newClients / base.leads,
            medianDaysToBook: base.medianDays,
            revenueFirst,
            revenue90d: round2(revenueFirst * blendedMultiplier),
            lostClients: base.lost
        };
    });

    const totals = rows.reduce(
        (acc, row) => ({
            leads: acc.leads + row.leads,
            newClients: acc.newClients + row.newClients,
            revenueFirst: round2(acc.revenueFirst + row.revenueFirst),
            revenue90d: round2(acc.revenue90d + row.revenue90d),
            lostClients: acc.lostClients + row.lostClients
        }),
        { leads: 0, newClients: 0, revenueFirst: 0, revenue90d: 0, lostClients: 0 }
    );

    let netActive = 410;
    const movement = COHORT_BASE.map(base => {
        netActive += base.newClients - base.lost;
        return { month: base.month, gained: base.newClients, lost: -base.lost, netActive };
    });

    return {
        rows,
        totals: {
            ...totals,
            convRate: totals.leads === 0 ? 0 : totals.newClients / totals.leads,
            medianDaysToBook: 4
        },
        movement
    };
};

export const formatCurrency = (amount: number): string =>
    `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export const formatPercentage = (value: number): string => `${(value * 100).toFixed(1)}%`;
